/* LogsControlComponent.js */

"use strict";

const EventEmitter = require("events").EventEmitter;
const _ = require("lodash");
const LogsControlRenderer = require("./LogsControlRenderer");
const DEFAULT_LOG_LEVELS = {
    INFO: true,
    CHECKPOINT: true,
    TASK: true,
    RESPONSE: true,
    DEBUG: true,
    WARNING: true,
};
const LOG_LEVELS = require("./LogLevels").LOG_LEVELS;
const LOG_SEQUENCE = require("./LogSequence").LOG_SEQUENCE;
const DEFAULT_LOG_SEQUENCE = LOG_SEQUENCE.LOG_EVENT_NUMBER;
const CONTROL_MIN_HEIGHT = 100;

class LogsControlComponent extends EventEmitter {
    /**
     * @schema.name Logs Control
     * @schema.package @gluenetworks/logs_control
     * @schema.packageIdentifier LogsControlComponent
     * @schema.namespace require("@gluenetworks/logs_control").LogsControlComponent
     *
     * @schema.configuration.logs.name logs
     * @schema.configuration.logs.type Array
     * @schema.configuration.logs.default []
     * @schema.configuration.logs.required true
     *
     * @schema.configuration.logLevels.name logLevels
     * @schema.configuration.logLevels.type Object
     * @schema.configuration.logLevels.default {}
     * @schema.configuration.logLevels.required false
     *
     * @schema.configuration.whiteListedTags.name whiteListedTags
     * @schema.configuration.whiteListedTags.type Array
     * @schema.configuration.whiteListedTags.default ['FRE', 'type', 'AttachedStorage', 'Watchdog']
     * @schema.configuration.whiteListedTags.required false
     *
     * @schema.configuration.searchString.name searchString
     * @schema.configuration.searchString.type String
     * @schema.configuration.searchString.default undefined
     * @schema.configuration.searchString.required false
     *
     * @schema.configuration.searchCaseSensitive.name searchCaseSensitive
     * @schema.configuration.searchCaseSensitive.type Boolean
     * @schema.configuration.searchCaseSensitive.default false
     * @schema.configuration.searchCaseSensitive.required false
     *
     * @schema.configuration.logSequence.name logSequence
     * @schema.configuration.logSequence.type String
     * @schema.configuration.logSequence.default log_event_number
     * @schema.configuration.logSequence.required false
     *
     * @schema.configuration.controlHeight.name controlHeight
     * @schema.configuration.controlHeight.type Integer
     * @schema.configuration.controlHeight.default 300
     * @schema.configuration.controlHeight.required false
     *
     * @schema.configuration.actionBarVisible.name actionBarVisible
     * @schema.configuration.actionBarVisible.type Boolean
     * @schema.configuration.actionBarVisible.default true
     * @schema.configuration.actionBarVisible.required false
     *
     * @schema.configuration.scrollLock.name scrollLock
     * @schema.configuration.scrollLock.type Boolean
     * @schema.configuration.scrollLock.default false
     * @schema.configuration.scrollLock.required false
     *
     * @schema.configuration.buildActionBar.name buildActionBar
     * @schema.configuration.buildActionBar.type Boolean
     * @schema.configuration.buildActionBar.default true
     * @schema.configuration.buildActionBar.required false
     *
     */
    constructor(config) {
        super(config);
        this._firstConnectionId = undefined;
        this.logLevels = DEFAULT_LOG_LEVELS;
        if (config.logLevels && !_.isEmpty(config.logLevels)) {
            this.setLogLevels(config.logLevels);
        }
        this._whiteListedTags = _.get(config, "whiteListedTags", []);
        this._searchString = config.searchString ? config.searchString : "";
        this._searchCaseSensitive = config.searchCaseSensitive === true;
        this._logSequence =
            config.logSequence && LOG_SEQUENCE.isValidSequence(config.logSequence)
                ? config.logSequence
                : DEFAULT_LOG_SEQUENCE.value;
        let tranformedLogsResults = this._transformLogs(_.isEmpty(config.logs) ? [] : config.logs);
        this._logs = tranformedLogsResults.logs;
        this._maxLlnLength = tranformedLogsResults.maxLlnLength;
        this._controlHeight =
            _.isNumber(config.controlHeight) && config.controlHeight > CONTROL_MIN_HEIGHT
                ? config.controlHeight
                : CONTROL_MIN_HEIGHT;
        this._buildActionBar = _.get(config, "buildActionBar", true);
        if (!this._buildActionBar) {
            this._actionBarVisible = false;
        } else {
            this._actionBarVisible = config.actionBarVisible === false ? false : true;
        }
        this._scrollLock = _.get(config, "scrollLock", false);
        this._renderer = new LogsControlRenderer(this);

        // let logs = require('../../sample_logs/DeviceDiscoveryLogWithNegativeTimes').logs;
        // this.setLogs(logs);
        // this.setLogs(logs.slice(0,1254));
        // setTimeout(() => {
        //     this.appendLogs(logs.slice(1254));
        // },15000);
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS */
    /*******************************************/
    _registerListeners() {}

    _unregisterListeners() {}

    _triggerLogFiltersChangedEvent() {
        this.emit("logFiltersChanged", this.logLevels);
    }

    _triggerLogSequenceChangedEvent() {
        this.emit("logSequenceChanged", this.logSequence);
    }

    _triggerUserSearchResultsCountChanged(newUserSearchResultsCount, oldUserSearchResultsCount) {
        this.emit(
            "UserSearchResultsCountChanged",
            newUserSearchResultsCount,
            oldUserSearchResultsCount
        );
    }

    _triggerCurrentSearchStringSequenceNumberChanged(
        newCurrentSearchStringSequenceNumberChanged,
        oldCurrentSearchStringSequenceNumberChanged
    ) {
        this.emit(
            "CurrentSearchStringSequenceNumberChanged",
            newCurrentSearchStringSequenceNumberChanged,
            oldCurrentSearchStringSequenceNumberChanged
        );
    }

    _triggerScrollLockChangedEvent() {
        this.emit("scrollLockChanged", this.scrollLock);
    }

    _transformLogs(logs) {
        let logsByLineNumber = [];
        let maxLlnLength = 0;
        for (let index = 0; index < logs.length; index++) {
            let currentLog = logs[index];
            if (currentLog.priv === true) {
                continue;
            }
            let logInfo = {};
            let msg = currentLog.msg ? currentLog.msg : currentLog.resp ? currentLog.resp : "";
            msg = currentLog.eventType ? currentLog.eventType : msg;
            let msgSplitIntoLines = msg.split(/\n|\r/g);
            logInfo.depth = currentLog.depth ? currentLog.depth : "";
            logInfo.time = currentLog.time ? currentLog.time : "";
            logInfo.tags = currentLog.tags ? this._getPrunedTags(currentLog.tags) : [];
            logInfo.lln = currentLog.lln;
            logInfo.lvl = currentLog.lvl;
            logInfo.msg = msgSplitIntoLines[0];
            logInfo.taskDuration = currentLog.taskDuration;
            logInfo.previousLogEventTime = logs[index - 1] ? logs[index - 1].time : undefined;
            logInfo.fn = currentLog.fn ? currentLog.fn : undefined;
            logInfo.fln = currentLog.fln ? currentLog.fln : undefined;
            logsByLineNumber.push(logInfo);
            let currentLlnLength = (_.get(currentLog, "lln", "") + "").length;
            maxLlnLength = currentLlnLength > maxLlnLength ? currentLlnLength : maxLlnLength;
            let depth = logInfo.depth ? logInfo.depth : 0;
            let lvlNotation = "",
                depthTab;
            if (currentLog.lvl) {
                lvlNotation = LOG_LEVELS[currentLog.lvl].notation;
                if (currentLog.lvl === "TASK") {
                    let taskNotations = LOG_LEVELS[currentLog.lvl].notation;
                    lvlNotation =
                        "taskDuration" in currentLog ? taskNotations.end : taskNotations.start;
                    depth = depth - 1;
                }
            } else if (currentLog.resp) {
                logInfo.lvl = "RESPONSE";
                depth = 1;
                if (currentLog.cnId) {
                    this._firstConnectionId = this._firstConnectionId
                        ? this._firstConnectionId
                        : currentLog.cnId;
                    logInfo.connectionId = currentLog.cnId - this._firstConnectionId + 1;
                } else {
                    logInfo.connectionId = 1;
                }
                lvlNotation = logInfo.connectionId + LOG_LEVELS[logInfo.lvl].notation;
                logInfo.connectionInfo = _.get(currentLog, ["tags", "^ConnectionInfo"], undefined);
            }
            depthTab = "  ".repeat(depth);
            logInfo.msg = depthTab + lvlNotation + " " + logInfo.msg;

            for (let lineIndex = 1; lineIndex < msgSplitIntoLines.length; lineIndex++) {
                if (
                    !(lineIndex === msgSplitIntoLines.length - 1 && !msgSplitIntoLines[lineIndex])
                ) {
                    let newLineMsgInfo = {
                        msg: depthTab + lvlNotation + " " + msgSplitIntoLines[lineIndex],
                    };
                    newLineMsgInfo.depth = logInfo.depth;
                    newLineMsgInfo.lvl = logInfo.lvl;
                    newLineMsgInfo.isPartOfAnotherLogMsg = true;
                    newLineMsgInfo.lln = "";
                    newLineMsgInfo.time = logInfo.time;
                    logsByLineNumber.push(newLineMsgInfo);
                }
            }
        }
        return {logs: logsByLineNumber, maxLlnLength: maxLlnLength};
    }

    _transformAppendedLogs(logs) {
        let transformedLogsResults = this._transformLogs(logs);
        let transformedLogs = transformedLogsResults.logs;
        let index = this.logs.length - 1;
        this._maxLlnLength =
            transformedLogsResults.maxLlnLength > this._maxLlnLength
                ? transformedLogsResults.maxLlnLength
                : this._maxLlnLength;
        while (index >= 0 ? !transformedLogs[0].previousLogEventTime : false) {
            let currentLog = this.logs[index];
            if (!currentLog.isPartOfAnotherLogMsg) {
                transformedLogs[0].previousLogEventTime = currentLog.time;
            }
            index--;
        }
        return transformedLogs;
    }

    _getPrunedTags(tags) {
        let tagsList = tags ? Object.keys(tags) : [];
        let prunedTags = [];
        for (let index = 0; index < tagsList.length; index++) {
            let isStartingWithHash = tagsList[index].match(/^#/) !== null;
            if (isStartingWithHash || this._whiteListedTags.includes(tagsList[index])) {
                prunedTags.push(tagsList[index]);
            }
        }
        return prunedTags;
    }
    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS */
    /* START PUBLIC COMPONENT MODEL METHODS */
    /*******************************************/

    get renderer() {
        return this.getRenderer();
    }
    getRenderer() {
        return this._renderer;
    }

    shutdown() {
        this._unregisterListeners();
        if (!this.renderer) {
            return Promise.resolve();
        } else {
            return this._renderer.shutdown().then(
                () => {
                    this._firstConnectionId = undefined;
                    this._logs = [];
                    this._searchString = undefined;
                    this._searchCaseSensitive = undefined;
                    this._controlHeight = undefined;
                    this._logSequence = undefined;
                    this._renderer = undefined;
                },
                () => {
                    //Error handling on renderer shut down
                }
            );
        }
    }
    /*******************************************/
    /* END COMPONENT MODEL METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE METHODS */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE METHODS */
    /* START PUBLIC API METHODS */
    /*******************************************/

    /**
     * @schema.methods.appendLogs.name appendLogs
     * @schema.methods.appendLogs.description Appends logs and returns an instance of the control for chainability
     * @schema.methods.appendLogs.returns Instance of the control
     * @schema.methods.appendLogs.parameters.logs.name logs
     * @schema.methods.appendLogs.parameters.logs.description array of logs or single array object to append
     * @schema.methods.appendLogs.parameters.logs.type Array|Object
     */
    appendLogs(logs) {
        if (!_.isEmpty(logs) && _.isObject(logs)) {
            let logsArray = _.isArray(logs) ? logs : [logs];
            let appendLogs = this._transformAppendedLogs(logsArray);
            this._logs = this.logs.concat(appendLogs);
            this.emit("appendLogs", appendLogs);
        }
        return this;
    }

    /**
     * @schema.methods.setLogs.name setLogs
     * @schema.methods.setLogs.description Sets logs and returns an instance of the control for chainability
     * @schema.methods.setLogs.returns Instance of the control
     * @schema.methods.setLogs.parameters.logs.name logs
     * @schema.methods.setLogs.parameters.logs.description The new logs
     * @schema.methods.setLogs.parameters.logs.type Array
     */
    setLogs(logs) {
        if (_.isArray(logs)) {
            this._logs = [];
            this._firstConnectionId = undefined;
            let tranformedLogsResults = this._transformLogs(logs);
            let transformedLogs = tranformedLogsResults.logs;
            this._logs = this._logs.concat(transformedLogs);
            this._maxLlnLength = tranformedLogsResults.maxLlnLength;
            this.emit("newLogs", this._logs);
        }
        return this;
    }

    set logs(logs) {
        return this.setLogs(logs);
    }

    /**
     * @schema.methods.getLogs.name getLogs
     * @schema.methods.getLogs.description Returns logs of the control
     * @schema.methods.getLogs.returns Array
     */
    getLogs() {
        return this._logs.slice(0);
    }

    get logs() {
        return this.getLogs();
    }

    /**
     * @schema.methods.setLogLevels.name setLogLevels
     * @schema.methods.setLogLevels.description Set log levels active/inactive to hide/show logs by their log levels
     * @schema.methods.setLogLevels.returns Instance of the control
     * @schema.methods.setLogLevels.parameters.newLogLevels.name newLogLevels
     * @schema.methods.setLogLevels.parameters.newLogLevels.description new log levels
     * @schema.methods.setLogLevels.parameters.newLogLevels.type {Object}
     */
    setLogLevels(newLogLevels) {
        let cleanedLogLevels = _.merge(
            _.clone(this._logLevels),
            _.pick(newLogLevels, Object.keys(newLogLevels))
        );
        if (!_.isEqual(this._logLevels, cleanedLogLevels)) {
            this._logLevels = cleanedLogLevels;
            if (this.renderer) {
                this.emit("logLevels", this._logLevels);
            }
        }
        return this;
    }

    /**
     * @schema.methods.getLogLevels.name getLogLevels
     * @schema.methods.getLogLevels.description Returns log levels with boolean value indicating if a level is filtered out
     * @schema.methods.getLogLevels.returns Object
     */
    getLogLevels() {
        return this._logLevels;
    }

    set logLevels(newLogLevels) {
        return this.setLogLevels(newLogLevels);
    }

    get logLevels() {
        return this.getLogLevels();
    }

    /**
     * @schema.methods.setWhiteListedTags.name setWhiteListedTags
     * @schema.methods.setWhiteListedTags.description Set white listed tags shown on this control UI
     * @schema.methods.setWhiteListedTags.returns Instance of the control
     * @schema.methods.setWhiteListedTags.parameters.newTags.name newTags
     * @schema.methods.setWhiteListedTags.parameters.newTags.description new white listed tags list
     * @schema.methods.setWhiteListedTags.parameters.newTags.type Array
     */
    setWhiteListedTags(newTags) {
        if (_.isArray(newTags)) {
            this._whiteListedTags = newTags.slice(0);
            this.emit("newWhiteListedTags", newTags);
        }
        return this;
    }

    /**
     * @schema.methods.getWhiteListedTags.name getWhiteListedTags
     * @schema.methods.getWhiteListedTags.description Retruns list of tags shown on this control UI
     * @schema.methods.getWhiteListedTags.returns Array
     */
    getWhiteListedTags() {
        return this._whiteListedTags.slice(0);
    }

    set whiteListedTags(newTags) {
        return this.setWhiteListedTags(newTags);
    }

    get whiteListedTags() {
        return this.getWhiteListedTags();
    }

    /**
     * @schema.methods.setTagFilters.name setTagFilters
     * @schema.methods.setTagFilters.description Set tag fiters to hide/show logs by tags if any
     * @schema.methods.setTagFilters.returns Instance of the control
     * @schema.methods.setTagFilters.parameters.newTagFilters.name newTagFilters
     * @schema.methods.setTagFilters.parameters.newTagFilters.description new tag filters
     * @schema.methods.setTagFilters.parameters.newTagFilters.type {Array}
     */
    setTagFilters(newTagFilters) {
        if (!_.isEmpty(newTagFilters)) {
            this.emit("tagFilters", newTagFilters);
        }
        return this;
    }

    /**
     * @schema.methods.getTagFilters.name getTagFilters
     * @schema.methods.getTagFilters.description Returns tag filters with boolean value indicating if logs with a tag associated is filtered out
     * @schema.methods.getTagFilters.returns {Array}
     */
    getTagFilters() {
        return this.renderer._uniqueTagList.slice(0);
    }

    /**
     * @schema.methods.setSearchString.name setSearchString
     * @schema.methods.setSearchString.description Set string to search logs on UI
     * @schema.methods.setSearchString.returns Instance of the control
     * @schema.methods.setSearchString.parameters.newSearchString.name newSearchString
     * @schema.methods.setSearchString.parameters.newSearchString.description new search string
     * @schema.methods.setSearchString.parameters.newSearchString.type String
     */
    setSearchString(newSearchString, userTriggered = false) {
        if (this._searchString !== newSearchString) {
            this._searchString = newSearchString ? newSearchString : "";
            if (!userTriggered) {
                this.emit("searchString", newSearchString);
            }
        }
        return this;
    }

    /**
     * @schema.methods.getSearchString.name getSearchString
     * @schema.methods.getSearchString.description Returns log search string
     * @schema.methods.getSearchString.returns String
     */
    getSearchString() {
        return this._searchString;
    }

    set searchString(newSearchString) {
        return this.setSearchString(newSearchString);
    }

    get searchString() {
        return this.getSearchString();
    }

    /**
     * @schema.methods.setSearchCaseSensitive.name setSearchCaseSensitive
     * @schema.methods.setSearchCaseSensitive.description Decides if the search is case sensitive
     *                                              if set to true - search is case sensitive
     *                                              else - search is NOT case sensitive
     * @schema.methods.setSearchCaseSensitive.returns Instance of the control
     * @schema.methods.setSearchCaseSensitive.parameters.isSearchCaseSensitive.name isSearchCaseSensitive
     * @schema.methods.setSearchCaseSensitive.parameters.isSearchCaseSensitive.description new search case sensitive property
     * @schema.methods.setSearchCaseSensitive.parameters.isSearchCaseSensitive.type String
     */
    setSearchCaseSensitive(isSearchCaseSensitive, userTriggered = false) {
        if (
            isSearchCaseSensitive !== this._searchCaseSensitive &&
            _.isBoolean(isSearchCaseSensitive)
        ) {
            this._searchCaseSensitive = isSearchCaseSensitive;
            if (!userTriggered) {
                this.emit("searchCaseSensitive", this._searchCaseSensitive);
            }
        }
        return this;
    }

    /**
     * @schema.methods.isSearchCaseSensitive.name isSearchCaseSensitive
     * @schema.methods.isSearchCaseSensitive.description Returns if the search is case sensitive
     * @schema.methods.isSearchCaseSensitive.returns String
     */
    isSearchCaseSensitive() {
        return this._searchCaseSensitive;
    }

    set searchCaseSensitive(isSearchCaseSensitive) {
        return this.setSearchCaseSensitive(isSearchCaseSensitive);
    }

    get searchCaseSensitive() {
        return this.isSearchCaseSensitive();
    }

    /**
     * @schema.methods.setLogSequence.name setLogSequence
     * @schema.methods.setLogSequence.description set log sequence displayed on logs UI.
     *  Options - line number, log event number, time stamp, event duration etc
     * @schema.methods.setLogSequence.returns Instance of the control
     * @schema.methods.setLogSequence.parameters.newLogSequence.name newLogSequence
     * @schema.methods.setLogSequence.parameters.newLogSequence.description new log sequence selection
     * @schema.methods.setLogSequence.parameters.newLogSequence.type String
     */
    setLogSequence(newLogSequence) {
        let logSequence = LOG_SEQUENCE.isValidSequence(newLogSequence)
            ? newLogSequence
            : this._logSequence;
        if (logSequence !== this._logSequence) {
            this._logSequence = logSequence;
            this.emit("logSequence", this._logSequence);
        }
        return this;
    }

    /**
     * @schema.methods.getLogSequence.name getLogSequence
     * @schema.methods.getLogSequence.description Returns log sequence displayed on each log
     * @schema.methods.getLogSequence.returns String
     */
    getLogSequence() {
        return this._logSequence;
    }

    get logSequence() {
        return this.getLogSequence();
    }

    set logSequence(newLogSequence) {
        return this.setLogSequence(newLogSequence);
    }

    /**
     * @schema.methods.setControlHeight.name setControlHeight
     * @schema.methods.setControlHeight.description set control with given height
     * @schema.methods.setControlHeight.parameters.newHeight.name newHeight
     * @schema.methods.setControlHeight.parameters.newHeight.description new height of control
     * @schema.methods.setControlHeight.parameters.newHeight.type {Integer}
     */
    setControlHeight(newHeight) {
        if (this._controlHeight !== newHeight && _.isNumber(newHeight)) {
            this._controlHeight = newHeight > CONTROL_MIN_HEIGHT ? newHeight : CONTROL_MIN_HEIGHT;
            this.emit("controlHeight", newHeight);
        }
        return this;
    }

    /**
     * @schema.methods.getControlHeight.name getControlHeight
     * @schema.methods.getControlHeight.description Returns control height in pixels
     * @schema.methods.getControlHeight.returns String
     */
    getControlHeight() {
        return this._controlHeight;
    }

    set controlHeight(newHeight) {
        return this.setControlHeight(newHeight);
    }

    get controlHeight() {
        return this.getControlHeight();
    }

    /**
     * @schema.methods.setActionBarVisible.name setActionBarVisible
     * @schema.methods.setActionBarVisible.description show/hide action bar on UI
     * @schema.methods.setActionBarVisible.returns Instance of this control
     * @schema.methods.setActionBarVisible.parameters.visible.name visible
     * @schema.methods.setActionBarVisible.parameters.visible.description if true show action bar else hide it
     * @schema.methods.setActionBarVisible.parameters.visible.type {Boolean}
     */
    setActionBarVisible(visible) {
        if (visible !== this._actionBarVisible && _.isBoolean(visible)) {
            this._actionBarVisible = visible;
            this.emit("actionBarVisible", this._actionBarVisible);
        }
        return this;
    }

    /**
     * @schema.methods.isActionBarVisible.name isActionBarVisible
     * @schema.methods.isActionBarVisible.description Returns if action bar is visible on UI
     * @schema.methods.isActionBarVisible.returns Boolean
     */
    isActionBarVisible() {
        return this._actionBarVisible;
    }

    set actionBarVisible(visible) {
        return this.setActionBarVisible(visible);
    }

    get actionBarVisible() {
        return this.isActionBarVisible();
    }

    /**
     * @schema.methods.toggleActionBar.name toggleActionBar
     * @schema.methods.toggleActionBar.description Toggles to show/hide action bar on UI
     * @schema.methods.toggleActionBar.returns Instance of this control
     */
    toggleActionBar() {
        this.setActionBarVisible(!this.actionBarVisible);
        return this;
    }

    /**
     * @schema.methods.setScrollLock.name setScrollLock
     * @schema.methods.setScrollLock.description if true do not scroll to end of text on append logs
     * @schema.methods.setScrollLock.returns Instance of this control
     * @schema.methods.setScrollLock.parameters.scrollLock.name scrollLock
     * @schema.methods.setScrollLock.parameters.scrollLock.description if true do not scroll to end of text on append logs
     * @schema.methods.setScrollLock.parameters.scrollLock.type {Boolean}
     */
    setScrollLock(scrollLock) {
        if (this._scrollLock !== scrollLock) {
            this._scrollLock = scrollLock === true;
            this.emit("scrollLock", scrollLock);
        }
        return this;
    }

    /**
     * @schema.methods.getScrollLock.name getScrollLock
     * @schema.methods.getScrollLock.description Returns if editor scrolls to bottom on appending logs
     * @schema.methods.getScrollLock.returns Boolean
     */
    getScrollLock() {
        return this._scrollLock;
    }

    set scrollLock(scrollLock) {
        return this.setScrollLock(scrollLock);
    }

    get scrollLock() {
        return this.getScrollLock();
    }

    /**
     * @schema.methods.getBuildActionBar.name getBuildActionBar
     * @schema.methods.getBuildActionBar.description Returns if action bar is built on UI
     * @schema.methods.getBuildActionBar.returns Boolean
     */
    getBuildActionBar() {
        return this._buildActionBar;
    }

    get buildActionBar() {
        return this.getBuildActionBar();
    }

    /**
     * @schema.methods.scrollTop.name scrollTop
     * @schema.methods.scrollTop.description scrolls to last row of editor
     * @schema.methods.scrollTop.returns Instance of this control
     */
    scrollTop() {
        this.renderer.scrollTop();
        return this;
    }

    /**
     * @schema.methods.highlightNextSearchStringMatch.name highlightNextSearchStringMatch
     * @schema.methods.highlightNextSearchStringMatch.description If a valid searchString is set, highlights the next search string
     * @schema.methods.highlightNextSearchStringMatch.returns Instance of this control
     */
    highlightNextSearchStringMatch() {
        if (!_.isEmpty(this._searchString)) {
            this.emit("highlightNextSearchStringMatch");
            return this;
        }
    }

    /**
     * @schema.methods.highlightPreviousSearchStringMatch.name highlightPreviousSearchStringMatch
     * @schema.methods.highlightPreviousSearchStringMatch.description If a valid searchString is set, highlights the prev search string
     * @schema.methods.highlightPreviousSearchStringMatch.returns Instance of this control
     */
    highlightPreviousSearchStringMatch() {
        if (!_.isEmpty(this._searchString)) {
            this.emit("highlightPreviousSearchStringMatch");
            return this;
        }
    }

    /**
     * @schema.methods.getSearchStringMatchCount.name getSearchStringMatchCount
     * @schema.methods.getSearchStringMatchCount.description If a valid searchString is set, returns number of matches found
     * @schema.methods.getSearchStringMatchCount.returns {Number}
     */
    getSearchStringMatchCount() {
        return this.renderer.getSearchStringMatchCount();
    }

    /**
     * @schema.methods.getHighlightedSearchStringMatchSequenceNumber.name getHighlightedSearchStringMatchSequenceNumber
     * @schema.methods.getHighlightedSearchStringMatchSequenceNumber.description If a valid searchString is set, returns current highlighted match sequence number
     * @schema.methods.getHighlightedSearchStringMatchSequenceNumber.returns {Number}
     */
    getHighlightedSearchStringMatchSequenceNumber() {
        return this.renderer.getHighlightedSearchStringMatchSequenceNumber();
    }
    /*******************************************/
    /* END PUBLIC API METHODS */
    /*******************************************/
}

module.exports = LogsControlComponent;
